import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import PageTitle from '../components/PageTitle';
import { 
  ArrowRight, 
  Flame, 
  Users, 
  Calendar, 
  Trophy,
  Star,
  Check,
  ChevronRight,
  Play,
  Clock,
  Dumbbell,
  ShoppingBag,
  Tv,
  Smartphone,
  Award
} from 'lucide-react';

const LandingPage = () => {
  // Lagree machines for sale
  const machines = [
    {
      id: "micro",
      name: "Lagree Micro",
      tagline: "The Home Studio Essential",
      price: 4990,
      monthlyPrice: 167,
      image: "https://images.unsplash.com/photo-1638805981949-362f5964521e?q=85&w=800&auto=format&fit=crop",
      features: [
        "Compact design (84\" x 24\")",
        "Perfect for home studios",
        "Full Megaformer functionality",
        "Easy storage & transport"
      ],
      bestseller: true
    },
    {
      id: "mini-pro",
      name: "Lagree Mini Pro",
      tagline: "Professional Performance",
      price: 6490,
      monthlyPrice: 217,
      image: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=85&w=800&auto=format&fit=crop",
      features: [
        "Studio-grade construction",
        "Enhanced spring system",
        "Extended carriage range",
        "Premium components"
      ],
      bestseller: false
    }
  ];

  // Real certified Lagree instructors
  const instructors = [
    {
      name: "Danielle Powers",
      title: "Senior Lagree Instructor",
      bio: "15+ years mastering the Lagree Method. Specializes in Time Under Tension techniques and core transformation.",
      image: "https://images.unsplash.com/photo-1594381898411-846e7d193883?q=85&w=400&auto=format&fit=crop",
      certifications: ["Lagree Master Trainer", "CPR/AED Certified"],
      classCount: 2500
    },
    {
      name: "Michelle Rodriguez",
      title: "Lagree Master Trainer",
      bio: "Former professional dancer bringing grace and precision to every slow, controlled movement on the Megaformer.",
      image: "https://images.unsplash.com/photo-1574230951449-2635f5cb3bc6?q=85&w=400&auto=format&fit=crop",
      certifications: ["Lagree Certified Level 3", "Movement Specialist"],
      classCount: 1800
    },
    {
      name: "Amanda Chen",
      title: "Elite Lagree Coach",
      bio: "Expert in effective resistance training. Her classes focus on muscle activation and the mind-body connection.",
      image: "https://images.unsplash.com/photo-1609505848912-b7c3b8b4beda?q=85&w=400&auto=format&fit=crop",
      certifications: ["Lagree Certified", "Anatomy Specialist"],
      classCount: 1200
    }
  ];

  // Subscription plans
  const plans = [
    {
      name: "MONTHLY",
      price: "$49",
      period: "/month",
      features: [
        "Unlimited live classes daily",
        "Full on-demand library (500+ classes)",
        "Progress & streak tracking",
        "iOS & Android app access",
        "Community forum access"
      ],
      popular: false
    },
    {
      name: "ANNUAL",
      price: "$399",
      period: "/year",
      savings: "Save $189",
      features: [
        "Everything in Monthly",
        "2 months FREE",
        "Priority live class booking",
        "Exclusive masterclasses",
        "Early access to new content"
      ],
      popular: true
    }
  ];

  // Features/benefits
  const benefits = [
    {
      icon: <Flame className="w-6 h-6" />,
      title: "DAILY LIVE CLASSES",
      description: "Join real-time sessions with certified Lagree instructors every day"
    },
    {
      icon: <Play className="w-6 h-6" />,
      title: "ON-DEMAND LIBRARY",
      description: "500+ classes featuring slow, controlled movements on the Megaformer"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "TIME UNDER TENSION",
      description: "Master the signature Lagree technique for maximum muscle activation"
    },
    {
      icon: <Trophy className="w-6 h-6" />,
      title: "TRACK YOUR PROGRESS",
      description: "Monitor your transformation with detailed analytics and streaks"
    }
  ];

  return (
    <div className="min-h-screen bg-[#090D0F]">
      <PageTitle />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo="/" size="default" />
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#machines" className="text-zinc-400 hover:text-white transition-colors">Shop Machines</a>
            <a href="#method" className="text-zinc-400 hover:text-white transition-colors">The Method</a>
            <a href="#instructors" className="text-zinc-400 hover:text-white transition-colors">Instructors</a>
            <a href="#pricing" className="text-zinc-400 hover:text-white transition-colors">Membership</a>
          </div>

          <div className="flex items-center gap-4">
            <Link to="/instructor">
              <Button variant="ghost" className="text-zinc-500 hover:text-zinc-300 text-sm" data-testid="nav-instructor-login">
                Instructor Login
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="text-zinc-400 hover:text-white" data-testid="nav-login">
                Sign In
              </Button>
            </Link>
            <Link to="/shop">
              <Button className="bg-teal-400 text-black font-bold uppercase tracking-wider hover:bg-teal-300" data-testid="nav-shop">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Shop
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1754257320300-e159f7cfa864?q=85&w=1920&auto=format&fit=crop"
            alt="Lagree Megaformer workout"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#090D0F] via-[#090D0F]/85 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-2 bg-teal-400/20 text-teal-400 text-sm font-bold uppercase tracking-wider rounded-sm mb-6">
              The Official Home Lagree Experience
            </span>
            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
              THE AUTHENTIC<br />
              <span className="text-teal-400">LAGREE STUDIO</span><br />
              NOW IN YOUR HOME
            </h1>
            <p className="text-xl text-zinc-400 mb-8 max-w-lg">
              Own the official Lagree machine and get unlimited access to live and on-demand classes from the world's most elite certified instructors. This is <span className="text-white font-semibold">Lagree MVMT</span>.
            </p>
            <p className="text-sm text-zinc-500 mb-6 border-l-2 border-teal-400 pl-4">
              <strong className="text-zinc-300">Note:</strong> The Lagree Method is not Pilates. It's a high-intensity, low-impact workout designed for maximum muscle transformation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/shop">
                <Button className="h-14 px-8 bg-teal-400 text-black font-bold uppercase tracking-wider hover:bg-teal-300" data-testid="hero-cta">
                  <ShoppingBag className="mr-2 w-5 h-5" />
                  Shop Machines
                </Button>
              </Link>
              <Link to="/classes">
                <Button variant="outline" className="h-14 px-8 border-zinc-700 text-white hover:bg-zinc-800" data-testid="explore-classes">
                  Explore Classes
                </Button>
              </Link>
            </div>

            <div className="flex items-center gap-8 mt-12">
              <div>
                <div className="text-3xl font-bold text-teal-400">10K+</div>
                <div className="text-zinc-500 text-sm">Home Studios</div>
              </div>
              <div className="w-px h-12 bg-zinc-800" />
              <div>
                <div className="text-3xl font-bold">500+</div>
                <div className="text-zinc-500 text-sm">Classes</div>
              </div>
              <div className="w-px h-12 bg-zinc-800" />
              <div>
                <div className="text-3xl font-bold">30+</div>
                <div className="text-zinc-500 text-sm">Certified Instructors</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Equipment Section - NEW */}
      <section className="py-24 px-6 bg-[#0A0C0E]" id="machines">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-400 text-sm font-bold uppercase tracking-wider mb-4 block">Step 1</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">YOUR LAGREE MACHINE AWAITS</h2>
            <p className="text-zinc-500 text-lg max-w-2xl mx-auto">
              The authentic Megaformer experience, engineered for your home. Choose your machine and start your transformation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {machines.map((machine) => (
              <div
                key={machine.id}
                className={`relative bg-[#121416] border rounded-sm overflow-hidden transition-all hover:border-teal-400/50 ${
                  machine.bestseller ? 'border-teal-400' : 'border-zinc-800'
                }`}
              >
                {machine.bestseller && (
                  <span className="absolute top-4 right-4 px-3 py-1 bg-teal-400 text-black text-xs font-bold uppercase rounded-sm z-10">
                    Bestseller
                  </span>
                )}
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={machine.image}
                    alt={machine.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121416] to-transparent" />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold mb-1">{machine.name}</h3>
                  <p className="text-teal-400 text-sm mb-4">{machine.tagline}</p>
                  
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-3xl font-bold">${machine.price.toLocaleString()}</span>
                    <span className="text-zinc-500">or from</span>
                    <span className="text-teal-400 font-semibold">${machine.monthlyPrice}/mo</span>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {machine.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-zinc-400">
                        <Check className="w-4 h-4 text-teal-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="flex gap-3">
                    <Link to={`/shop/${machine.id}`} className="flex-1">
                      <Button className="w-full h-12 bg-teal-400 text-black font-bold uppercase tracking-wider hover:bg-teal-300">
                        Shop Now
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                    <Link to={`/shop/${machine.id}`}>
                      <Button variant="outline" className="h-12 px-4 border-zinc-700 hover:bg-zinc-800">
                        Learn More
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-zinc-500 mb-4">Financing available through Affirm • Free shipping on all machines</p>
            <Link to="/shop" className="text-teal-400 hover:text-teal-300 font-medium inline-flex items-center gap-2">
              View All Machines <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* The Method Section */}
      <section className="py-24 px-6" id="method">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-teal-400 text-sm font-bold uppercase tracking-wider mb-4 block">The Lagree Method</span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                NOT PILATES.<br />
                <span className="text-teal-400">LAGREE.</span>
              </h2>
              <p className="text-zinc-400 text-lg mb-6">
                Created by fitness visionary Sebastien Lagree, the Lagree Method is a high-intensity, low-impact workout performed on the patented Megaformer. Unlike Pilates, Lagree focuses on <span className="text-white font-semibold">Time Under Tension</span>—slow, controlled movements that keep muscles engaged for maximum transformation.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-400/20 rounded-sm flex items-center justify-center text-teal-400 flex-shrink-0">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Time Under Tension</h4>
                    <p className="text-zinc-500 text-sm">Slow 4-count movements maximize muscle fiber recruitment</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-400/20 rounded-sm flex items-center justify-center text-teal-400 flex-shrink-0">
                    <Dumbbell className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Effective Resistance</h4>
                    <p className="text-zinc-500 text-sm">Spring-loaded carriage provides variable resistance throughout each move</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-400/20 rounded-sm flex items-center justify-center text-teal-400 flex-shrink-0">
                    <Trophy className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Total Body Transformation</h4>
                    <p className="text-zinc-500 text-sm">Signature moves like Elevator Lunge, Catfish, and Scrambled Eggs target every muscle</p>
                  </div>
                </div>
              </div>
              <Link to="/classes">
                <Button className="h-12 px-6 bg-white text-black font-bold uppercase tracking-wider hover:bg-zinc-200">
                  Try Your First Class
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1754257319787-4c19f30f2a16?q=85&w=800&auto=format&fit=crop"
                alt="Lagree Method workout"
                className="rounded-sm w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-teal-400 text-black p-6 rounded-sm">
                <div className="text-4xl font-bold">45</div>
                <div className="text-sm uppercase tracking-wider">Min Classes</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features/Benefits Grid */}
      <section className="py-24 px-6 bg-[#0A0C0E]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-400 text-sm font-bold uppercase tracking-wider mb-4 block">Step 2</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">UNLIMITED CLASSES. ONE SUBSCRIPTION.</h2>
            <p className="text-zinc-500 text-lg max-w-2xl mx-auto">
              Get access to daily live classes and our complete on-demand library of Lagree workouts
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-[#121416] border border-zinc-800 rounded-sm p-6 hover:border-teal-400/30 transition-colors"
              >
                <div className="w-12 h-12 bg-teal-400/20 rounded-sm flex items-center justify-center text-teal-400 mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-bold mb-2">{benefit.title}</h3>
                <p className="text-zinc-500 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>

          <div className="flex justify-center gap-8 mt-12">
            <div className="flex items-center gap-3 text-zinc-400">
              <Tv className="w-5 h-5" />
              <span>Stream on TV</span>
            </div>
            <div className="flex items-center gap-3 text-zinc-400">
              <Smartphone className="w-5 h-5" />
              <span>iOS & Android</span>
            </div>
            <div className="flex items-center gap-3 text-zinc-400">
              <Calendar className="w-5 h-5" />
              <span>Daily Live Classes</span>
            </div>
          </div>
        </div>
      </section>

      {/* Instructors Section */}
      <section className="py-24 px-6" id="instructors">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-400 text-sm font-bold uppercase tracking-wider mb-4 block">Your Coaches</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">CERTIFIED LAGREE INSTRUCTORS</h2>
            <p className="text-zinc-500 text-lg max-w-2xl mx-auto">
              Learn from elite trainers certified in the authentic Lagree Method—not Pilates instructors, but dedicated Lagree professionals.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {instructors.map((instructor, index) => (
              <div
                key={index}
                className="group bg-[#121416] border border-zinc-800 rounded-sm overflow-hidden hover:border-teal-400/30 transition-colors"
              >
                <div className="aspect-[4/5] relative overflow-hidden">
                  <img
                    src={instructor.image}
                    alt={instructor.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121416] via-transparent to-transparent" />
                </div>
                <div className="p-6 -mt-20 relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="w-4 h-4 text-teal-400" />
                    <span className="text-teal-400 text-xs font-medium uppercase tracking-wider">{instructor.title}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">{instructor.name}</h3>
                  <p className="text-zinc-500 text-sm mb-4">{instructor.bio}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {instructor.certifications.map((cert, i) => (
                      <span key={i} className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded-sm">
                        {cert}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                    <div className="text-sm">
                      <span className="text-teal-400 font-bold">{instructor.classCount.toLocaleString()}</span>
                      <span className="text-zinc-500"> classes taught</span>
                    </div>
                    <Link to={`/instructor/${instructor.name.toLowerCase().replace(' ', '-')}`} className="text-teal-400 hover:text-teal-300 text-sm font-medium">
                      View Schedule →
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/instructors" className="text-teal-400 hover:text-teal-300 font-medium inline-flex items-center gap-2">
              Meet All Instructors <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6 bg-[#0A0C0E]" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-400 text-sm font-bold uppercase tracking-wider mb-4 block">Membership</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">UNLIMITED CLASSES. ONE SUBSCRIPTION.</h2>
            <p className="text-zinc-500 text-lg max-w-2xl mx-auto">
              Choose the plan that fits your commitment. Machine purchase not required to subscribe.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative rounded-sm p-8 ${
                  plan.popular 
                    ? 'bg-teal-400/10 border-2 border-teal-400' 
                    : 'bg-[#121416] border border-zinc-800'
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-teal-400 text-black text-sm font-bold uppercase rounded-sm">
                    Best Value
                  </span>
                )}
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-zinc-500">{plan.period}</span>
                </div>
                {plan.savings && (
                  <span className="inline-block px-3 py-1 bg-teal-400/20 text-teal-400 text-sm font-medium rounded-sm mb-4">
                    {plan.savings}
                  </span>
                )}
                <ul className="space-y-4 mb-8 mt-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-teal-400 flex-shrink-0" />
                      <span className="text-zinc-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button 
                    className={`w-full h-12 font-bold uppercase tracking-wider ${
                      plan.popular 
                        ? 'bg-teal-400 text-black hover:bg-teal-300' 
                        : 'bg-white text-black hover:bg-zinc-200'
                    }`}
                    data-testid={`pricing-${plan.name.toLowerCase()}`}
                  >
                    Start 7-Day Free Trial
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <p className="text-center text-zinc-600 text-sm mt-4">No credit card required for trial</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            READY TO TRANSFORM?
          </h2>
          <p className="text-xl text-zinc-400 mb-4">
            Get your Lagree machine and start your journey today.
          </p>
          <p className="text-zinc-500 mb-8">
            Join 10,000+ members experiencing the authentic Lagree Method at home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop">
              <Button className="h-14 px-12 bg-teal-400 text-black font-bold uppercase tracking-wider hover:bg-teal-300" data-testid="footer-shop-cta">
                <ShoppingBag className="mr-2 w-5 h-5" />
                Shop Machines
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="h-14 px-12 border-zinc-700 text-white hover:bg-zinc-800" data-testid="footer-trial-cta">
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-zinc-800 bg-[#0A0C0E]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <Logo linkTo="/" size="default" />
              <p className="text-zinc-500 text-sm mt-4">
                The authentic Lagree studio experience, now in your home.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Shop</h4>
              <ul className="space-y-2 text-zinc-500 text-sm">
                <li><Link to="/shop" className="hover:text-white transition-colors">All Machines</Link></li>
                <li><Link to="/shop/micro" className="hover:text-white transition-colors">Lagree Micro</Link></li>
                <li><Link to="/shop/mini-pro" className="hover:text-white transition-colors">Mini Pro</Link></li>
                <li><Link to="/shop/accessories" className="hover:text-white transition-colors">Accessories</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Classes</h4>
              <ul className="space-y-2 text-zinc-500 text-sm">
                <li><Link to="/classes" className="hover:text-white transition-colors">On-Demand Library</Link></li>
                <li><Link to="/schedule" className="hover:text-white transition-colors">Live Schedule</Link></li>
                <li><Link to="/instructors" className="hover:text-white transition-colors">Instructors</Link></li>
                <li><Link to="/programs" className="hover:text-white transition-colors">Programs</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Support</h4>
              <ul className="space-y-2 text-zinc-500 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Shipping & Returns</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Machine Setup Guide</a></li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-8 border-t border-zinc-800">
            <div className="flex items-center gap-6 text-zinc-500 text-sm">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">Accessibility</a>
            </div>
            <div className="text-zinc-600 text-sm">
              © 2026 Lagree MVMT. All rights reserved. Lagree® and Megaformer® are registered trademarks.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
